import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    MenuItem,
    TextField,
    Typography,
    Container,
    Paper,
    FormHelperText,
    Chip,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    FormControl,
    Alert,
    Divider,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Grid,
    IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MainLayoutDemand from "../MainLoyout/MainLayoutDemand";
import Api from "../../Services/Api";

const CreateSupplyFeedPanel = () => {
    const isAdmin = useSelector((state) => state.users.is_admin); // Check if the user is an admin
    const [formData, setFormData] = useState({
        name: '',
        title: '', // Title field
        link: '',
        cap: '1000',
        source: 'taboola',
        type: 'N2S Yahoo',
        country: 'US',
        device: 'all', // Device field
        identifier: '',
        notes: '',
        partner: '', // Partner field
    });

    const [partners, setPartners] = useState([]); // List of partners
    const [loadingPartners, setLoadingPartners] = useState(false); // Loading state for partners
    const [openPreview, setOpenPreview] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [copySuccess, setCopySuccess] = useState('');
    const navigate = useNavigate();

    // Define available macros for Taboola
    const taboolaMacros = [
        { name: '{q}', description: 'Keyword macro (required)' },
        { name: '{title}', description: 'Title of the content' },
        { name: '{campaign_id}', description: 'Taboola campaign ID' },
        { name: '{campaign_item_id}', description: 'Taboola campaign item ID' },
        { name: '{tblci}', description: 'Taboola click ID' },
        { name: '{site_id}', description: 'Taboola site ID' },
        { name: '{site}', description: 'Taboola site name' }
    ];

    // Define available macros for Facebook
    const facebookMacros = [
        { name: '{q}', description: 'Keyword macro (required)' },
        { name: '{ad_id}', description: 'Facebook ad ID' },
        { name: '{adset_id}', description: 'Facebook ad set ID' },
        { name: '{campaign_id}', description: 'Facebook campaign ID' },
        { name: '{ad_name}', description: 'Facebook ad name' },
        { name: '{adset_name}', description: 'Facebook ad set name' },
        { name: '{campaign_name}', description: 'Facebook campaign name' },
        { name: '{placement}', description: 'Facebook placement' },
        { name: '{fbclid}', description: 'Facebook click ID' }
    ];

    // Define available macros for Outbrain
    const outbrainMacros = [
        { name: '{q}', description: 'Keyword macro (required)' }
    ];

    // Get macros based on selected source
    const getSourceMacros = () => {
        switch(formData.source) {
            case 'taboola':
                return { macros: taboolaMacros, title: 'Taboola' };
            case 'facebook':
                return { macros: facebookMacros, title: 'Facebook' };
            case 'outbrain':
                return { macros: outbrainMacros, title: 'Outbrain' };
            default:
                return { macros: [], title: '' };
        }
    };

    // Function to copy macro to clipboard
    const copyMacroToClipboard = (macro) => {
        navigator.clipboard.writeText(macro)
            .then(() => {
                setCopySuccess(macro);
                setTimeout(() => setCopySuccess(''), 2000);
            })
            .catch(err => {
                console.error('Failed to copy: ', err);
            });
    };

    // Function to insert macro into link field
    const insertMacroToLink = (macro) => {
        setFormData({ ...formData, link: formData.link + macro });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Fetch partners if user is admin
    useEffect(() => {
        if (isAdmin) {
            fetchPartners();
        }
    }, [isAdmin]);

    const fetchPartners = async () => {
        setLoadingPartners(true);
        try {
            const response = await Api.demandPartners(); // API call to fetch partners
            setPartners(response || []);
        } catch (error) {
            console.error('Failed to fetch partners:', error);
            setSnackbarMessage('Failed to load partners');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoadingPartners(false);
        }
    };

    const handlePreview = (e) => {
        e.preventDefault();

        // Basic required field validation
        if (!formData.link || !formData.identifier) {
            setSnackbarMessage('All fields are required!');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (isAdmin && !formData.partner) {
            setSnackbarMessage('Please select a partner. This field is required for admin users.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (!formData.link.includes('{q}')) {
            setSnackbarMessage('The link must include the {q} keyword macro.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // Additional validation for Taboola source
        if (formData.source === 'taboola') {
            // No additional validation required for now, but we can add specific Taboola validation here if needed
        }

        setOpenPreview(true);
    };

    const handleClose = () => {
        setOpenPreview(false);
    };

    const handleConfirmCreate = async () => {
        try {
            const payload = { ...formData };

            // If the user is an admin, the partner is required in the payload
            if (isAdmin && !formData.partner) {
                setSnackbarMessage('Partner selection is mandatory for admin users.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }

            const response = await Api.createSpinSupplyFeed(payload); // API call
            if (response.errors) {
                setSnackbarMessage(Object.values(response.errors).join(', '));
                setSnackbarSeverity('error');
            } else {
                setSnackbarMessage('Feed created successfully!');
                setSnackbarSeverity('success');
                setOpenPreview(false); // Close modal after success
                navigate('/spin/list'); // Redirect to the list
            }
        } catch (error) {
            console.error('Error:', error);
            setSnackbarMessage('Failed to create feed. Please try again.');
            setSnackbarSeverity('error');
        } finally {
            setSnackbarOpen(true);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const typeToSourceMap = {
        'YHS': ['TypeIn'], // YHS only allows "Type In"
        'N2S Yahoo': ['taboola', 'facebook', 'outbrain'], // N2S Yahoo restricted sources
        'N2S Bing': ['taboola', 'facebook', 'outbrain'], // N2S Bing restricted sources
        default: ['taboola', 'facebook', 'outbrain', 'TypeIn'], // Default fallback for any other types
    };

    // Component to display available macros
    const MacrosDisplay = () => {
        const { macros, title } = getSourceMacros();
        return (
            <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    Available Macros for {title}
                    <Tooltip title={`These macros can be used in your URL to track ${title}-specific parameters`}>
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Typography>
                <Paper 
                    variant="outlined" 
                    sx={{ 
                        p: 2, 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        borderColor: '#e0e0e0'
                    }}
                >
                    <Grid container spacing={1}>
                        {macros.map((macro) => (
                            <Grid item xs={12} sm={6} md={4} key={macro.name}>
                                <Chip
                                    label={macro.name}
                                    color={macro.name === '{q}' ? 'primary' : 'default'}
                                    variant={macro.name === '{q}' ? 'filled' : 'outlined'}
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 'medium',
                                        mr: 1,
                                        mb: 1,
                                        '&:hover': {
                                            backgroundColor: macro.name === '{q}' ? '#1565c0' : '#e0e0e0',
                                        },
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                        height: '36px',
                                        width: '100%',
                                        justifyContent: 'space-between',
                                    }}
                                    onClick={() => insertMacroToLink(macro.name)}
                                    deleteIcon={
                                        <Tooltip title="Copy to clipboard">
                                            <ContentCopyIcon fontSize="small" />
                                        </Tooltip>
                                    }
                                    onDelete={() => copyMacroToClipboard(macro.name)}
                                />
                                {copySuccess === macro.name && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', ml: 1 }}>
                                        Copied!
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
                                    {macro.description}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Box>
        );
    };

    return (
        <MainLayoutDemand>
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Stack direction="row" justifyContent="right" alignItems="center" spacing={1}>
                        <Chip
                            label="Fast Track VIP Access"
                            color="secondary"
                            icon={<FlashOnIcon />}
                            sx={{ fontSize: '1rem', fontWeight: 'bold', py: 1, px: 2 }}
                        />
                    </Stack>
                    <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                        Feed Creation
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                        Welcome to the Fast Track VIP program! Once your supply feed is created, you will start
                        receiving traffic within <strong>5 minutes</strong>.
                    </Typography>
                    <Box component="form" onSubmit={handlePreview} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {isAdmin && (
                            <FormControl fullWidth error={isAdmin && !formData.partner}>
                                <Autocomplete
                                    options={partners}
                                    getOptionLabel={(option) => option.name || ''}
                                    loading={loadingPartners}
                                    onChange={(e, newValue) => setFormData({ ...formData, partner: newValue?.id || '' })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Partner"
                                            placeholder="Search partner"
                                            required={isAdmin}
                                            helperText={isAdmin && !formData.partner ? 'This field is required for admin users.' : ''}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingPartners ? <CircularProgress size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>
                        )}

                        <FormControl fullWidth>
                            <TextField
                                label="Type"
                                name="type"
                                value={formData.type}
                                onChange={(e) => {
                                    const newType = e.target?.value || ''; // Ensure newType is always a string
                                    setFormData({
                                        ...formData,
                                        type: newType,
                                        source: typeToSourceMap[newType]?.[0] || typeToSourceMap.default[0], // Fallback to default if newType is not found
                                    });
                                }}
                                select
                                required
                            >
                                <MenuItem value="N2S Yahoo">N2S Yahoo</MenuItem>
                                <MenuItem value="N2S Bing">N2S Bing</MenuItem>
                                <MenuItem value="YHS">YHS</MenuItem>
                            </TextField>
                            <FormHelperText>Select the type of N2S.</FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                            <TextField
                                label="Traffic Source"
                                name="source"
                                value={formData.source}
                                onChange={(e) => handleChange(e)}
                                select
                                required
                                disabled={typeToSourceMap[formData.type]?.length === 1} // Disable dropdown if only one option available
                            >
                                {(typeToSourceMap[formData.type] || typeToSourceMap.default).map((source) => (
                                    <MenuItem key={source} value={source}>
                                        {source.charAt(0).toUpperCase() + source.slice(1)} {/* Capitalize first letter */}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <FormHelperText>Select the traffic source for this supply feed.</FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                            <TextField
                                label="Device"
                                name="device"
                                value={formData.device}
                                onChange={handleChange}
                                select
                                required
                            >
                                <MenuItem value="all">Mobile & Desktop</MenuItem>
                                <MenuItem value="mobile">Only Mobile</MenuItem>
                                <MenuItem value="desktop">Only Desktop</MenuItem>

                            </TextField>
                            <FormHelperText>Select the device for this supply feed.</FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                            <TextField
                                label="Country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                select
                                required
                            >
                                <MenuItem value="US">United States (US)</MenuItem>
                                <MenuItem value="GB">United Kingdom (GB)</MenuItem>
                                <MenuItem value="DE">Germany (DE)</MenuItem>
                                <MenuItem value="KR">South Korea (KR)</MenuItem>
                                <MenuItem value="JP">Japan (JP)</MenuItem>
                                <MenuItem value="FR">France (FR)</MenuItem>
                                <MenuItem value="CA">Canada (CA)</MenuItem>
                                <MenuItem value="IT">Italy (IT)</MenuItem>
                                <MenuItem value="ES">Spain (ES)</MenuItem>
                                <MenuItem value="NL">Netherlands (NL)</MenuItem>
                                <MenuItem value="NO">Norway (NO)</MenuItem>
                                <MenuItem value="SE">Sweden (SE)</MenuItem>
                                <MenuItem value="DK">Denmark (DK)</MenuItem>
                                <MenuItem value="FI">Finland (FI)</MenuItem>
                                <MenuItem value="AU">Australia (AU)</MenuItem>
                                <MenuItem value="NZ">New Zealand (NZ)</MenuItem>
                                <MenuItem value="BR">Brazil (BR)</MenuItem>
                                <MenuItem value="TW">Taiwan (TW)</MenuItem>
                            </TextField>
                            <FormHelperText>Select the country for this supply feed.</FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                            <TextField
                                label="Supply Feed Link"
                                name="link"
                                type="url"
                                value={formData.link}
                                onChange={handleChange}
                                required
                                multiline
                                rows={2}
                                placeholder="Enter URL"
                            />
                            <FormHelperText>
                                Enter your supply feed URL with appropriate macros
                            </FormHelperText>
                        </FormControl>

                        {/* Display macros based on selected source */}
                        {['taboola', 'facebook', 'outbrain'].includes(formData.source) && (
                            <>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>{getSourceMacros().title}-specific macros available!</strong> Click on any macro to insert it into your URL, or click the copy icon to copy to clipboard.
                                    </Typography>
                                </Alert>
                                <MacrosDisplay />
                            </>
                        )}

                        <FormControl fullWidth>
                            <TextField
                                label="Identifier"
                                name="identifier"
                                value={formData.identifier}
                                onChange={handleChange}
                                required
                                placeholder="Enter unique identifier"
                            />
                            <FormHelperText>This is your API identifier for reporting purposes.</FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                            <TextField
                                label="Cap (Searches Per Day)"
                                name="cap"
                                value={formData.cap}
                                onChange={handleChange}
                                select
                                required
                            >
                                {[1000, 3000, 5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000].map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value.toLocaleString()} searches/day
                                    </MenuItem>
                                ))}
                            </TextField>
                            <FormHelperText>Select the daily search cap for this feed.</FormHelperText>
                        </FormControl>

                        <FormControl fullWidth>
                            <TextField
                                label="Notes"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Add any notes (optional)"
                            />
                        </FormControl>

                        <Button type="submit" variant="contained" color="primary" size="large" fullWidth>
                            Preview Supply Feed
                        </Button>
                    </Box>
                </Paper>

                <Dialog open={openPreview} onClose={handleClose} fullWidth maxWidth="md">
                    <DialogTitle sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1.5rem', 
                        textAlign: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        pb: 2,
                        backgroundColor: '#f8f9fa'
                    }}>
                        Preview Supply Feed Details
                    </DialogTitle>
                    <DialogContent sx={{ p: 0 }}>
                        <Box sx={{ p: 3 }}>
                            {/* Header section with key information */}
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    borderRadius: '12px', 
                                    backgroundColor: '#f0f7ff',
                                    border: '1px solid #cce5ff'
                                }}
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" justifyContent="space-between">
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                        <Typography variant="overline" color="text.secondary">
                                            FEED TYPE
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            {formData.type || 'N/A'}
                                        </Typography>
                                        <Chip 
                                            label={formData.source?.toUpperCase() || 'N/A'} 
                                            size="small" 
                                            color="primary" 
                                            sx={{ mt: 1 }}
                                        />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                        <Typography variant="overline" color="text.secondary">
                                            DAILY CAP
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            {formData.cap ? `${parseInt(formData.cap).toLocaleString()} searches` : 'N/A'}
                                        </Typography>
                                        <Chip 
                                            label={formData.country || 'N/A'} 
                                            size="small" 
                                            color="secondary" 
                                            sx={{ mt: 1, mr: 1 }}
                                        />
                                        <Chip 
                                            label={formData.device === 'all' ? 'Mobile & Desktop' : formData.device || 'N/A'} 
                                            size="small" 
                                            color="default" 
                                            sx={{ mt: 1 }}
                                        />
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Detailed information */}
                            <Grid container spacing={3}>
                                {/* Left column */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', height: '100%', border: '1px solid #e0e0e0' }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#424242' }}>
                                            Feed Configuration
                                        </Typography>
                                        
                                        {isAdmin && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Partner
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                    {partners.find((p) => p.id === formData.partner)?.name || 'N/A'}
                                                </Typography>
                                                <Divider sx={{ my: 1.5 }} />
                                            </Box>
                                        )}
                                        
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Identifier
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                {formData.identifier || 'N/A'}
                                            </Typography>
                                            <Divider sx={{ my: 1.5 }} />
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Notes
                                            </Typography>
                                            <Typography variant="body1" sx={{ 
                                                fontStyle: formData.notes ? 'normal' : 'italic',
                                                color: formData.notes ? 'text.primary' : 'text.secondary'
                                            }}>
                                                {formData.notes || 'No notes provided'}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                                
                                {/* Right column */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', height: '100%', border: '1px solid #e0e0e0' }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#424242' }}>
                                            URL Configuration
                                        </Typography>
                                        
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Supply Feed Link
                                            </Typography>
                                            <Paper 
                                                elevation={0} 
                                                sx={{ 
                                                    p: 2, 
                                                    mt: 1, 
                                                    backgroundColor: '#f5f5f5', 
                                                    borderRadius: '8px',
                                                    maxHeight: '150px',
                                                    overflow: 'auto'
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ 
                                                        wordBreak: 'break-word', 
                                                        fontFamily: 'monospace',
                                                        lineHeight: 1.6
                                                    }}
                                                >
                                                    {formData.link ? (
                                                        <>
                                                            {formData.link.split(/(\{[^}]+\})/).map((part, index) => {
                                                                // Check if the part is a macro (starts with { and ends with })
                                                                const isMacro = /^\{[^}]+\}$/.test(part);
                                                                return isMacro ? (
                                                                    <Chip
                                                                        key={index}
                                                                        label={part}
                                                                        size="small"
                                                                        color={part === '{q}' ? 'primary' : 'secondary'}
                                                                        sx={{ 
                                                                            fontFamily: 'monospace', 
                                                                            fontWeight: 'bold',
                                                                            mx: 0.5,
                                                                            my: 0.5
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span key={index}>{part}</span>
                                                                );
                                                            })}
                                                        </>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </Typography>
                                            </Paper>
                                            
                                            {/* URL validation indicators */}
                                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                                <Chip 
                                                    icon={<InfoOutlinedIcon fontSize="small" />}
                                                    label={`Contains {q} macro: ${formData.link.includes('{q}') ? 'Yes' : 'No'}`}
                                                    color={formData.link.includes('{q}') ? 'success' : 'error'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                
                                                <Chip 
                                                    icon={<InfoOutlinedIcon fontSize="small" />}
                                                    label={`Valid URL format`}
                                                    color={formData.link.startsWith('http') ? 'success' : 'error'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Stack>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ 
                        justifyContent: 'center', 
                        p: 3,
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#f8f9fa'
                    }}>
                        <Button
                            onClick={handleClose}
                            color="inherit"
                            variant="outlined"
                            size="large"
                            sx={{ 
                                textTransform: 'none', 
                                fontWeight: 'bold',
                                px: 4
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmCreate}
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{ 
                                textTransform: 'none', 
                                fontWeight: 'bold',
                                px: 4,
                                ml: 2
                            }}
                        >
                            Confirm & Create
                        </Button>
                    </DialogActions>
                </Dialog>
                <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
                    <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </MainLayoutDemand>
    );
};

export default CreateSupplyFeedPanel;